class CartDeals extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.cart-deals__track');
    this.viewport = this.querySelector('.cart-deals__viewport');
    this.slides = [...this.querySelectorAll('.cart-deals__slide')];
    this.dots = [...this.querySelectorAll('.cart-deals__dot')];
    this.index = 0;
    this.dragging = false;
    this.startX = 0;
    this.offset = 0;

    if (!this.track || !this.viewport || this.slides.length === 0) return;

    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerEnd = this.handlePointerEnd.bind(this);
    this.handleClick = this.handleClick.bind(this);

    this.track.addEventListener('pointerdown', this.handlePointerDown);
    this.track.addEventListener('pointermove', this.handlePointerMove);
    this.track.addEventListener('pointerup', this.handlePointerEnd);
    this.track.addEventListener('pointercancel', this.handlePointerEnd);
    this.addEventListener('click', this.handleClick);

    this.update(0);
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  normalizeIndex(index) {
    if (index < 0) return this.slides.length - 1;
    if (index >= this.slides.length) return 0;
    return index;
  }

  update(index, offset = 0) {
    this.index = this.normalizeIndex(index);
    this.track.style.transform = offset
      ? `translate3d(calc(-${this.index * 100}% + ${offset}px), 0, 0)`
      : `translate3d(-${this.index * 100}%, 0, 0)`;

    this.slides.forEach((slide, slideIndex) => {
      slide.setAttribute('aria-hidden', String(slideIndex !== this.index));
    });
    this.dots.forEach((dot, dotIndex) => {
      dot.classList.toggle('is-active', dotIndex === this.index);
    });
  }

  startAutoplay() {
    this.stopAutoplay();
    if (this.slides.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.autoplayTimer = window.setInterval(() => {
      this.update(this.index + 1);
    }, 4000);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  handlePointerDown(event) {
    if (event.button !== 0 || event.target.closest('.cart-deals__code')) return;

    this.dragging = true;
    this.startX = event.clientX;
    this.offset = 0;
    this.track.classList.add('is-dragging');
    this.stopAutoplay();
    this.track.setPointerCapture?.(event.pointerId);
  }

  handlePointerMove(event) {
    if (!this.dragging) return;

    this.offset = event.clientX - this.startX;
    this.update(this.index, this.offset);
    if (Math.abs(this.offset) > 4) event.preventDefault();
  }

  handlePointerEnd() {
    if (!this.dragging) return;

    this.dragging = false;
    this.track.classList.remove('is-dragging');
    const threshold = Math.max(40, this.viewport.clientWidth * 0.18);
    const nextIndex = this.offset < -threshold ? this.index + 1 : this.offset > threshold ? this.index - 1 : this.index;
    this.update(nextIndex);
    this.startAutoplay();
  }

  async handleClick(event) {
    const codeButton = event.target.closest('[data-cart-deals-code]');
    if (codeButton && this.contains(codeButton)) {
      event.preventDefault();
      await this.copyCode(codeButton);
      return;
    }

    const dot = event.target.closest('[data-cart-deals-index]');
    if (!dot || !this.contains(dot)) return;

    const index = Number.parseInt(dot.dataset.cartDealsIndex, 10);
    if (Number.isNaN(index)) return;
    this.stopAutoplay();
    this.update(index);
    this.startAutoplay();
  }

  async copyCode(button) {
    const code = button.dataset.cartDealsCode;
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = code;
      textarea.setAttribute('readonly', '');
      textarea.style.cssText = 'position:fixed;opacity:0;pointer-events:none;';
      document.body.append(textarea);
      textarea.select();
      document.execCommand('copy');
      textarea.remove();
    }

    const notice = button.nextElementSibling;
    if (!notice?.classList.contains('cart-deals__copy-notice')) return;
    notice.textContent = 'Copied';
    notice.classList.add('is-visible');
    window.setTimeout(() => {
      notice.classList.remove('is-visible');
      notice.textContent = '';
    }, 1500);
  }
}

if (!customElements.get('cart-deals')) {
  customElements.define('cart-deals', CartDeals);
}
